import { z } from 'zod';
import type { UserRoleToken } from '@sleed/bizcommerce-api';

/** The 12 role permission flags (UserRoleToken perm_* booleans), in display order. */
export const ROLE_PERMISSIONS = [
	'perm_dashboard',
	'perm_clg',
	'perm_prc',
	'perm_cust',
	'perm_ord',
	'perm_oe',
	'perm_oa',
	'perm_prompol',
	'perm_cms',
	'perm_rep',
	'perm_bookings',
	'perm_sys'
] as const;

export type RolePermission = (typeof ROLE_PERMISSIONS)[number];

const ROLE_PERMISSIONS_WITHOUT_APPOINTMENTS = ROLE_PERMISSIONS.filter(p => p !== 'perm_bookings');

/**
 * The permissions an administrator is shown: `perm_bookings` belongs to the optional Appointments module (BW-8453)
 * and is hidden while it is off. The form still round-trips every flag (`tokenToForm`/`formToToken`).
 */
export function visibleRolePermissions(appointmentsEnabled: boolean): readonly RolePermission[] {
	return appointmentsEnabled ? ROLE_PERMISSIONS : ROLE_PERMISSIONS_WITHOUT_APPOINTMENTS;
}

/**
 * User role edit form. Mirrors `UserRoleToken` and the legacy admin grid
 * (Core/client/admin/src/pages/System/UserRoles/index.js): nm required, si required, plus the
 * 12 perm_* permission booleans (shown as toggles in the editor, summarized in the grid).
 */
export const userRoleSchema = z.object({
	id: z.number().optional(),
	nm: z.string().trim().min(1, 'validation.required').max(100, 'validation.tooLong'),
	iv: z.boolean(),
	si: z.number({ message: 'validation.required' }).int(),
	perm_dashboard: z.boolean(),
	perm_clg: z.boolean(),
	perm_prc: z.boolean(),
	perm_cust: z.boolean(),
	perm_ord: z.boolean(),
	perm_oe: z.boolean(),
	perm_oa: z.boolean(),
	perm_prompol: z.boolean(),
	perm_cms: z.boolean(),
	perm_rep: z.boolean(),
	perm_bookings: z.boolean(),
	perm_sys: z.boolean()
});

export type UserRoleFormValues = z.infer<typeof userRoleSchema>;

export const EMPTY_USER_ROLE: UserRoleFormValues = {
	nm: '',
	iv: true,
	si: 0,
	perm_dashboard: false,
	perm_clg: false,
	perm_prc: false,
	perm_cust: false,
	perm_ord: false,
	perm_oe: false,
	perm_oa: false,
	perm_prompol: false,
	perm_cms: false,
	perm_rep: false,
	perm_bookings: false,
	perm_sys: false
};

export function tokenToForm(token: UserRoleToken): UserRoleFormValues {
	const perms = Object.fromEntries(ROLE_PERMISSIONS.map(p => [p, Boolean(token[p])])) as Record<RolePermission, boolean>;
	return { id: token.id, nm: token.nm ?? '', iv: token.iv ?? false, si: token.si ?? 0, ...perms };
}

export function formToToken(form: UserRoleFormValues): UserRoleToken {
	const perms = Object.fromEntries(ROLE_PERMISSIONS.map(p => [p, form[p]]));
	return { id: form.id, nm: form.nm.trim(), iv: form.iv, si: form.si, ...perms };
}

/** Count of granted permissions on a token among `permissions` (for the grid summary). */
export function grantedPermissionCount(token: UserRoleToken, permissions: readonly RolePermission[] = ROLE_PERMISSIONS): number {
	return permissions.reduce((n, p) => n + (token[p] ? 1 : 0), 0);
}
